import { Middleware, MiddlewareAPI, Action } from 'redux';
import { reduce, difference, transform } from 'lodash';
import core, { App, Entities, View, GQL } from '@gin-betting/core';

import { State } from 'types';
import {
    DEBUG_INFO_LOCALSTORAGE_KEY,
    TIME_UNIT_IN_MILLISECONDS,
} from 'app-constants';
import { matchLocators, betHistoryLocator } from './locators';
import { markReachableEntities } from './actions';
import {
    TraverseStateKeys,
    MARK_REACHABLE_ENTITIES,
    TRAVERSE_STATE_KEYS,
} from './constants';

const {
    entities: {
        actions: { deleteEntities },
        utils: { renormalize },
    },
} = core;

const GC_SCHEDULE_TIMEOUT = 0.5 * TIME_UNIT_IN_MILLISECONDS.minute;

function makeMiddleware(): Middleware {
    let timerId: number;

    return ({ dispatch, getState }: MiddlewareAPI<State>) => {
        const mark = () => {
            window.clearTimeout(timerId);
            dispatch(markReachableEntities(App.EntityNames.Matches));
        };

        if (localStorage.getItem(DEBUG_INFO_LOCALSTORAGE_KEY)) {
            (window as any).__app.dispatchGC = mark;
        }

        const sweep = (arg: FirstArgument<typeof deleteEntities>) => {
            dispatch(deleteEntities(arg));
        };

        const scheduleDeallocation = () => {
            timerId = window.setTimeout(mark, GC_SCHEDULE_TIMEOUT);
        };

        scheduleDeallocation();

        return next => (action: Action) => {
            switch (action.type) {
                case MARK_REACHABLE_ENTITIES: {
                    const state = getState();
                    const sweepableMatchEntities = locateSweepableMatches(
                        state
                    );

                    if (sweepableMatchEntities) {
                        sweep(sweepableMatchEntities);
                    }

                    scheduleDeallocation();

                    return;
                }

                default:
                    return next(action);
            }
        };
    };
}

const locateSweepableMatches = (
    state: State
): Entities.DeleteEntitiesPayload | void => {
    const entitiesState = state.get('entities');
    const cachedMatchIds = entitiesState
        .get('matches')
        // TODO: clear outright sport events
        .filter(({ type }) => GQL.SportEventType.MATCH === type)
        .keySeq()
        .toArray();

    const sweepableMatchIds = difference(
        cachedMatchIds,
        locateReachableMatchIds(state)
    );

    if (sweepableMatchIds.length === 0) {
        return;
    }

    const renormalized = renormalize(
        App.EntityNames.Matches,
        sweepableMatchIds,
        entitiesState
    );

    if (!renormalized) return;

    return transformRenormalizedToIds(renormalized.entities);
};

export const transformRenormalizedToIds = (
    entities: View.NormalizedEntities
): Entities.DeleteEntitiesPayload => {
    return transform(entities, (result, entitiesCollection, key) => {
        let value = entitiesCollection;

        if (key === 'tournaments') return result;
        if (key === 'slugs') {
            value = transform(value, (acc, slug: View.Slug, slugId) => {
                if (slug.matchId) {
                    acc[slugId] = slug;
                }

                return acc;
            });
        }

        result[key] = Object.keys(value);

        return result;
    });
};

const locateReachableMatchIds = (state: State): string[] => {
    return reduce(
        TRAVERSE_STATE_KEYS,
        (acc, key: TraverseStateKeys) => {
            if (key === 'betsHistory') {
                return acc.concat(betHistoryLocator(state));
            }

            const locateReachableMatches = matchLocators[key];

            // NOTE: async modules can be nullable. TODO: Improve "State" type GINFE-1830
            const stateTree: State[typeof key] | null = state.get(key);

            if (!stateTree) return acc;

            const reachableMatchIds = locateReachableMatches(
                stateTree as UnionToIntersection<typeof stateTree>,
                state.get('entities')
            );

            return acc.concat(reachableMatchIds);
        },
        [] as string[]
    );
};

export default makeMiddleware;
